import { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AuroraBackground } from '../components/shared/AuroraBackground';
import { BookOpen, BarChart3, Trophy, Swords, Target, Building2, Shield, ScrollText } from 'lucide-react';

// ─── Step definitions (mirrors real app flow) ───────────────────────────────

const STEPS = [
  { id: 'assess', label: 'Assess', warmth: 0 },
  { id: 'learn', label: 'Learn', warmth: 0.1 },
  { id: 'stats', label: 'Stats', warmth: 0.3 },
  { id: 'firm', label: 'Firm', warmth: 0.55 },
  { id: 'protocol', label: 'Protocol', warmth: 0.95, isPrescription: true },
];

// ─── Shield SVG ─────────────────────────────────────────────────────────────

function ShieldIcon({
  size = 24,
  filled = false,
  glowIntensity = 0,
}: {
  size?: number;
  filled?: boolean;
  glowIntensity?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      style={
        glowIntensity > 0
          ? { filter: `drop-shadow(0 0 ${4 + glowIntensity * 6}px rgba(232, 148, 58, ${glowIntensity * 0.5}))` }
          : undefined
      }
    >
      <path
        d="M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z"
        fill={filled ? 'rgba(232, 148, 58, 0.15)' : 'none'}
        stroke={filled ? '#E8943A' : 'rgba(255,255,255,0.2)'}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Ember burst particles ──────────────────────────────────────────────────

function EmberBurst({ count, intensity }: { count: number; intensity: number }) {
  const embers = Array.from({ length: count }, (_, i) => {
    const angle = (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.6;
    const distance = 50 + Math.random() * 70 * intensity;
    const size = 2 + Math.random() * 3;
    return {
      id: i,
      tx: Math.cos(angle) * distance,
      ty: Math.sin(angle) * distance,
      size,
      dur: 0.5 + Math.random() * 0.4,
      delay: Math.random() * 0.12,
      bright: Math.random() > 0.4,
    };
  });

  return (
    <>
      {embers.map((e) => (
        <motion.div
          key={e.id}
          className="absolute rounded-full"
          style={{
            width: e.size,
            height: e.size,
            backgroundColor: e.bright
              ? `rgba(255, 200, 100, ${0.6 + Math.random() * 0.4})`
              : `rgba(232, 148, 58, ${0.5 + Math.random() * 0.5})`,
            left: '50%',
            top: '50%',
            marginLeft: -e.size / 2,
            marginTop: -e.size / 2,
          }}
          initial={{ x: 0, y: 0, scale: 1, opacity: 1 }}
          animate={{ x: e.tx, y: e.ty, scale: 0, opacity: 0 }}
          transition={{ duration: e.dur, delay: e.delay, ease: 'easeOut' }}
        />
      ))}
    </>
  );
}

// ─── Forge Overlay (standard step transition) ───────────────────────────────

function ForgeOverlay({
  stepIndex,
  onComplete,
}: {
  stepIndex: number;
  onComplete: () => void;
}) {
  const emberCount = 5 + stepIndex * 4;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
    >
      {/* Backdrop */}
      <motion.div
        className="absolute inset-0 bg-[#060A12]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.94 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      />

      {/* Radial pulse */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.6, 0] }}
        transition={{ duration: 0.7, times: [0, 0.35, 1] }}
      >
        <div
          className="w-full h-full"
          style={{
            background: 'radial-gradient(circle at center, rgba(232, 148, 58, 0.12) 0%, transparent 45%)',
          }}
        />
      </motion.div>

      {/* Central forge moment */}
      <div className="relative z-10">
        <motion.div
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
        >
          <EmberBurst count={emberCount} intensity={1 + stepIndex * 0.25} />
        </motion.div>

        <motion.div
          className="relative"
          initial={{ scale: 0.4, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring' as const, stiffness: 350, damping: 22, delay: 0.05 }}
          onAnimationComplete={() => setTimeout(onComplete, 350)}
        >
          {/* Glow ring behind shield */}
          <motion.div
            className="absolute -inset-10 rounded-full"
            animate={{
              boxShadow: [
                '0 0 15px rgba(232, 148, 58, 0.08)',
                '0 0 50px rgba(232, 148, 58, 0.25)',
                '0 0 25px rgba(232, 148, 58, 0.12)',
              ],
            }}
            transition={{ duration: 0.5, times: [0, 0.5, 1] }}
          />

          {/* Shield heating up */}
          <motion.div
            animate={{
              filter: [
                'brightness(0.4) saturate(0.2)',
                'brightness(1.6) saturate(1.3)',
                'brightness(1.0) saturate(1.0)',
              ],
            }}
            transition={{ duration: 0.5, times: [0, 0.55, 1] }}
          >
            <ShieldIcon size={72} filled glowIntensity={0.8} />
          </motion.div>

          {/* Step name */}
          <motion.p
            className="absolute -bottom-9 left-1/2 -translate-x-1/2 text-[10px] font-mono text-[#E8943A]/60 tracking-[0.25em] uppercase whitespace-nowrap"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            {STEPS[stepIndex].label}
          </motion.p>
        </motion.div>
      </div>
    </motion.div>
  );
}

// ─── Prescription Forge (shields converge → shatter → reveal) ───────────────

function PrescriptionForge({ completedSteps, onComplete }: { completedSteps: number; onComplete: () => void }) {
  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.12 }}
    >
      <motion.div
        className="absolute inset-0 bg-[#060A12]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.96 }}
        exit={{ opacity: 0 }}
      />

      <div className="relative z-10">
        {/* Converging shields */}
        {Array.from({ length: completedSteps }, (_, i) => {
          const startX = (i - (completedSteps - 1) / 2) * 100;
          return (
            <motion.div
              key={i}
              className="absolute"
              style={{ left: '50%', top: '50%', marginLeft: -16, marginTop: -16 }}
              initial={{ x: startX, y: -15, scale: 1, opacity: 0.7 }}
              animate={{ x: 0, y: 0, scale: 0.7, opacity: 1 }}
              transition={{ duration: 0.35, delay: i * 0.06, ease: 'easeInOut' }}
            >
              <ShieldIcon size={32} filled glowIntensity={0.5} />
            </motion.div>
          );
        })}

        {/* Ember burst on convergence */}
        <motion.div
          className="absolute"
          style={{ left: '50%', top: '50%' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.45 }}
        >
          <EmberBurst count={24} intensity={2.5} />
        </motion.div>

        {/* Shatter fragments */}
        {[
          { x: -180, y: -160, r: -35 },
          { x: 180, y: -160, r: 35 },
          { x: -180, y: 160, r: -125 },
          { x: 180, y: 160, r: 125 },
        ].map((t, i) => (
          <motion.div
            key={`shard-${i}`}
            className="absolute"
            style={{
              left: '50%',
              top: '50%',
              marginLeft: -10,
              marginTop: -10,
              width: 20,
              height: 20,
              clipPath: [
                'polygon(50% 0%, 0% 100%, 50% 65%)',
                'polygon(50% 0%, 100% 100%, 50% 65%)',
                'polygon(0% 0%, 50% 35%, 0% 100%)',
                'polygon(100% 0%, 50% 35%, 100% 100%)',
              ][i],
              backgroundColor: '#E8943A',
            }}
            initial={{ x: 0, y: 0, scale: 1, opacity: 0.8, rotate: 0 }}
            animate={{ x: t.x, y: t.y, scale: 0.2, opacity: 0, rotate: t.r }}
            transition={{ delay: 0.5, duration: 0.45, ease: 'easeOut' }}
          />
        ))}

        {/* Flash */}
        <motion.div
          className="fixed inset-0 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.35, 0] }}
          transition={{ delay: 0.48, duration: 0.35, times: [0, 0.25, 1] }}
          onAnimationComplete={() => setTimeout(onComplete, 250)}
        >
          <div className="w-full h-full bg-[#E8943A]/15" />
        </motion.div>
      </div>
    </motion.div>
  );
}

// ─── Shield Progress Tracker ────────────────────────────────────────────────

function ShieldTracker({ currentStep, allComplete }: { currentStep: number; allComplete: boolean }) {
  return (
    <motion.div
      className="w-full max-w-[560px] mx-auto px-4 py-8"
      animate={
        allComplete
          ? { filter: 'drop-shadow(0 4px 16px rgba(232, 148, 58, 0.08))' }
          : { filter: 'drop-shadow(0 0 0 transparent)' }
      }
    >
      <div className="flex items-center justify-between">
        {STEPS.map((step, i) => {
          const isComplete = i < currentStep;
          const isCurrent = i === currentStep;
          return (
            <div key={step.id} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center">
                <motion.div
                  className="relative"
                  animate={isCurrent ? { scale: [1, 1.08, 1] } : { scale: 1 }}
                  transition={isCurrent ? { repeat: Infinity, duration: 2.5, ease: 'easeInOut' } : {}}
                >
                  <ShieldIcon
                    size={30}
                    filled={isComplete || isCurrent}
                    glowIntensity={isComplete ? 0.4 : isCurrent ? 0.2 : 0}
                  />
                  {/* Ember glow on completed */}
                  {isComplete && (
                    <motion.div
                      className="absolute inset-[-4px] rounded-full pointer-events-none"
                      animate={{
                        boxShadow: [
                          '0 0 4px rgba(232, 148, 58, 0.1)',
                          '0 0 10px rgba(232, 148, 58, 0.2)',
                          '0 0 4px rgba(232, 148, 58, 0.1)',
                        ],
                      }}
                      transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
                    />
                  )}
                </motion.div>
                <span
                  className={`
                    text-[9px] font-mono tracking-[0.15em] mt-2 whitespace-nowrap uppercase
                    ${isCurrent ? 'text-white/60' : isComplete ? 'text-[#E8943A]/45' : 'text-white/15'}
                  `}
                >
                  {step.label}
                </span>
              </div>

              {i < STEPS.length - 1 && (
                <motion.div
                  className="flex-1 h-px mx-2 mt-[-14px]"
                  animate={{
                    backgroundColor: isComplete
                      ? 'rgba(232, 148, 58, 0.2)'
                      : 'rgba(255,255,255,0.04)',
                  }}
                  transition={{ duration: 0.5 }}
                />
              )}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

// ─── Mock screen content (mimics real app screens) ──────────────────────────

function AssessContent() {
  const opts = [
    { icon: BookOpen, title: 'New to Trading', desc: 'Less than 6 months experience.' },
    { icon: BarChart3, title: 'Building My Track Record', desc: "6-12 months. I've been backtesting." },
    { icon: Trophy, title: 'Proven Live Trader', desc: '12+ months live prop firm history.' },
  ];
  return (
    <div className="max-w-[560px] mx-auto">
      <h1 className="text-xl md:text-2xl font-bold font-mono tracking-wide text-white/90 text-center mb-2">
        Where are you on your journey?
      </h1>
      <p className="text-[12px] font-mono text-white/30 text-center mb-8 tracking-wide">
        This helps us tailor your protocol.
      </p>
      <div className="space-y-3">
        {opts.map((o, i) => (
          <motion.div
            key={o.title}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.06 + i * 0.06, duration: 0.3 }}
          >
            <div className="bg-white/[0.02] border border-white/[0.05] rounded-xl p-5 backdrop-blur-md flex items-center gap-4 cursor-pointer hover:border-white/[0.12] hover:bg-white/[0.035] transition-all">
              <o.icon className="w-5 h-5 text-white/30 flex-shrink-0" />
              <div>
                <p className="text-sm font-mono font-medium text-white/70">{o.title}</p>
                <p className="text-[11px] font-mono text-white/25 mt-0.5">{o.desc}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function LearnContent() {
  return (
    <div className="max-w-[560px] mx-auto">
      <h1 className="text-xl md:text-2xl font-bold font-mono tracking-wide text-white/90 text-center mb-2">
        Choose Your Weapon
      </h1>
      <p className="text-[12px] font-mono text-white/30 text-center mb-8 tracking-wide">
        Two models. Pick the one that fits your edge.
      </p>
      <div className="grid grid-cols-2 gap-4">
        {[
          { icon: Swords, name: 'TCL Model', desc: 'Trend Continuation Ladder — high frequency pullback entries' },
          { icon: Target, name: 'SMOG Model', desc: 'Smart Money OG — structural reversals with outsized winners' },
        ].map((m, i) => (
          <motion.div
            key={m.name}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.06 + i * 0.08, duration: 0.3 }}
          >
            <div className="bg-white/[0.02] border border-white/[0.05] rounded-xl p-6 backdrop-blur-md cursor-pointer hover:border-white/[0.12] hover:bg-white/[0.035] transition-all text-center">
              <m.icon className="w-8 h-8 text-white/25 mx-auto mb-3" />
              <p className="text-sm font-mono font-medium text-white/70 mb-1">{m.name}</p>
              <p className="text-[10px] font-mono text-white/25 leading-relaxed">{m.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function StatsContent() {
  return (
    <div className="max-w-[480px] mx-auto">
      <h1 className="text-xl md:text-2xl font-bold font-mono tracking-wide text-white/90 text-center mb-2">
        Enter Your Stats
      </h1>
      <p className="text-[12px] font-mono text-white/30 text-center mb-8 tracking-wide">
        We need your track record to build your protocol.
      </p>
      <div className="bg-white/[0.02] border border-white/[0.05] rounded-xl p-6 backdrop-blur-md space-y-4">
        {['Total Trades', 'Win Rate (%)', 'Avg Winner (R)', 'Avg Loser (R)', 'Months Trading'].map((label, i) => (
          <motion.div
            key={label}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.06 + i * 0.05 }}
          >
            <label className="text-[10px] font-mono text-white/35 uppercase tracking-wider block mb-1.5">{label}</label>
            <div className="h-10 bg-white/[0.03] border border-white/[0.06] rounded-lg" />
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function FirmContent() {
  const firms = [
    { name: 'Apex Trader Funding', plans: '2 plans', sizes: '$25K – $150K' },
    { name: 'Topstep', plans: '2 plans', sizes: '$50K – $150K' },
    { name: 'Tradeify', plans: '3 plans', sizes: '$25K – $150K' },
    { name: 'My Funded Futures', plans: '3 plans', sizes: '$25K – $150K' },
  ];
  return (
    <div className="max-w-[660px] mx-auto">
      <h1 className="text-xl md:text-2xl font-bold font-mono tracking-wide text-white/90 text-center mb-2">
        Select Your Arena
      </h1>
      <p className="text-[12px] font-mono text-white/30 text-center mb-8 tracking-wide">
        Pick your prop firm and account size.
      </p>
      <div className="grid grid-cols-2 gap-3">
        {firms.map((f, i) => (
          <motion.div
            key={f.name}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.06 + i * 0.06, duration: 0.3 }}
          >
            <div className="bg-white/[0.02] border border-white/[0.05] rounded-xl p-5 backdrop-blur-md cursor-pointer hover:border-white/[0.12] hover:bg-white/[0.035] transition-all">
              <Building2 className="w-5 h-5 text-white/20 mb-2" />
              <p className="text-sm font-mono font-medium text-white/70">{f.name}</p>
              <div className="flex gap-3 mt-2">
                <span className="text-[10px] font-mono text-white/25">{f.plans}</span>
                <span className="text-[10px] font-mono text-white/25">{f.sizes}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function ProtocolContent() {
  const sections = [
    { id: 'header', fromX: -70, fromY: -50, fromR: -6 },
    { id: 'risk', fromX: 90, fromY: -35, fromR: 10 },
    { id: 'rules', fromX: -50, fromY: 70, fromR: -5 },
    { id: 'footer', fromX: 80, fromY: 55, fromR: 8 },
  ];

  return (
    <div className="max-w-[520px] mx-auto">
      <div className="relative">
        {/* Breathing glow unique to prescription */}
        <motion.div
          className="absolute -inset-3 rounded-2xl pointer-events-none z-0"
          animate={{
            boxShadow: [
              '0 0 30px rgba(232, 148, 58, 0.06), 0 0 60px rgba(232, 148, 58, 0.03)',
              '0 0 50px rgba(232, 148, 58, 0.12), 0 0 100px rgba(232, 148, 58, 0.05)',
              '0 0 30px rgba(232, 148, 58, 0.06), 0 0 60px rgba(232, 148, 58, 0.03)',
            ],
          }}
          transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
        />

        {/* SVG border trace */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none z-20 rounded-xl"
          viewBox="0 0 520 400"
          preserveAspectRatio="none"
        >
          <motion.rect
            x="1" y="1" width="518" height="398" rx="12"
            fill="none" stroke="#E8943A" strokeWidth="1"
            strokeDasharray="1836"
            initial={{ strokeDashoffset: 1836, opacity: 0.6 }}
            animate={{ strokeDashoffset: 0, opacity: 0.4 }}
            transition={{ duration: 1.0, ease: 'easeInOut', delay: 0.2 }}
          />
        </svg>

        <div className="relative bg-white/[0.03] border border-white/[0.06] rounded-xl p-8 backdrop-blur-md overflow-hidden z-10">
          {/* Inner warm gradient */}
          <div
            className="absolute inset-0 rounded-xl pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(232, 148, 58, 0.04) 0%, transparent 55%)' }}
          />

          <div className="relative z-10 space-y-5">
            {/* Header fragment */}
            <motion.div
              initial={{ x: sections[0].fromX, y: sections[0].fromY, rotate: sections[0].fromR, opacity: 0, scale: 0.7 }}
              animate={{ x: 0, y: 0, rotate: 0, opacity: 1, scale: 1 }}
              transition={{ type: 'spring' as const, stiffness: 220, damping: 22, delay: 0.15 }}
            >
              <p className="text-[10px] font-mono text-[#E8943A]/50 tracking-[0.2em] uppercase">Paladin Protocol</p>
              <p className="text-[10px] font-mono text-white/20 mt-1">TCL — Sword Tier &middot; Apex Trader Funding &middot; $50K</p>
            </motion.div>

            {/* Risk fragment */}
            <motion.div
              initial={{ x: sections[1].fromX, y: sections[1].fromY, rotate: sections[1].fromR, opacity: 0, scale: 0.7 }}
              animate={{ x: 0, y: 0, rotate: 0, opacity: 1, scale: 1 }}
              transition={{ type: 'spring' as const, stiffness: 220, damping: 22, delay: 0.25 }}
            >
              <div className="bg-white/[0.02] border border-white/[0.04] rounded-lg p-4">
                <span className="text-[10px] font-mono text-white/30 uppercase tracking-wider">Risk Per Trade</span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-3xl font-mono font-bold text-white">$187</span>
                  <span className="text-base font-mono text-white/40">1.5%</span>
                </div>
              </div>
            </motion.div>

            {/* Rules fragment */}
            <motion.div
              initial={{ x: sections[2].fromX, y: sections[2].fromY, rotate: sections[2].fromR, opacity: 0, scale: 0.7 }}
              animate={{ x: 0, y: 0, rotate: 0, opacity: 1, scale: 1 }}
              transition={{ type: 'spring' as const, stiffness: 220, damping: 22, delay: 0.35 }}
            >
              <div className="space-y-2.5">
                {[
                  { severity: 'high', text: 'Max 2 contracts per entry — hard cap' },
                  { severity: 'high', text: 'Hard stop at $1,000 daily loss limit' },
                  { severity: 'medium', text: '50% consistency rule — no single day > 50%' },
                  { severity: 'medium', text: 'No scaling into losing positions' },
                ].map((rule, i) => (
                  <motion.div
                    key={rule.text}
                    className="flex items-start gap-2.5"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.55 + i * 0.08 }}
                  >
                    <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${rule.severity === 'high' ? 'bg-[#E8943A]/70' : 'bg-white/20'}`} />
                    <span className="text-[12px] font-mono text-white/45 leading-relaxed">{rule.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Footer fragment */}
            <motion.div
              initial={{ x: sections[3].fromX, y: sections[3].fromY, rotate: sections[3].fromR, opacity: 0, scale: 0.7 }}
              animate={{ x: 0, y: 0, rotate: 0, opacity: 1, scale: 1 }}
              transition={{ type: 'spring' as const, stiffness: 220, damping: 22, delay: 0.45 }}
            >
              <motion.div
                className="h-px bg-gradient-to-r from-transparent via-[#E8943A]/15 to-transparent"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.7, duration: 0.5 }}
              />
              <p className="text-[10px] font-mono text-white/15 mt-3 text-center">
                Generated Apr 14, 2026. Regenerate if your firm, account size, or trading stats change.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Step content renderer ──────────────────────────────────────────────────

const SCREEN_CONTENT = [AssessContent, LearnContent, StatsContent, FirmContent, ProtocolContent];

// ─── Main Combined Preview ──────────────────────────────────────────────────

export function TransitionPreviewCombined() {
  const [currentStep, setCurrentStep] = useState(0);
  const [forgeStep, setForgeStep] = useState<number | null>(null);
  const [showPrescriptionForge, setShowPrescriptionForge] = useState(false);
  const [contentReady, setContentReady] = useState(true);
  const [contentKey, setContentKey] = useState(0);

  const step = STEPS[currentStep];
  const isPrescription = !!step.isPrescription;
  const ContentComponent = SCREEN_CONTENT[currentStep];

  const goToStep = useCallback(
    (next: number) => {
      if (next < 0 || next >= STEPS.length || next === currentStep || forgeStep !== null) return;

      setContentReady(false);

      if (next === STEPS.length - 1) {
        setShowPrescriptionForge(true);
      } else {
        setForgeStep(next);
      }
    },
    [currentStep, forgeStep],
  );

  const handleForgeComplete = useCallback(() => {
    const next = forgeStep!;
    setForgeStep(null);
    setCurrentStep(next);
    setContentKey((k) => k + 1);
    setTimeout(() => setContentReady(true), 80);
  }, [forgeStep]);

  const handlePrescriptionForgeComplete = useCallback(() => {
    setShowPrescriptionForge(false);
    setCurrentStep(STEPS.length - 1);
    setContentKey((k) => k + 1);
    setTimeout(() => setContentReady(true), 80);
  }, []);

  const handleBack = useCallback(() => {
    if (currentStep <= 0 || forgeStep !== null) return;
    setContentReady(false);
    setTimeout(() => {
      setCurrentStep(currentStep - 1);
      setContentKey((k) => k + 1);
      setTimeout(() => setContentReady(true), 80);
    }, 150);
  }, [currentStep, forgeStep]);

  return (
    <div className="relative min-h-screen overflow-hidden select-none bg-[#060A12]">
      {/* ── Real AuroraBackground with warmth evolution ── */}
      <AuroraBackground warmth={step.warmth} />

      {/* Edge vignette (from PageWrapper) */}
      <div className="absolute inset-0 pointer-events-none z-[1]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_50%,rgba(6,10,18,0.6)_100%)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#060A12]/50" />
      </div>

      {/* ── Forge overlays ── */}
      <AnimatePresence>
        {forgeStep !== null && (
          <ForgeOverlay stepIndex={forgeStep} onComplete={handleForgeComplete} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPrescriptionForge && (
          <PrescriptionForge completedSteps={STEPS.length - 1} onComplete={handlePrescriptionForgeComplete} />
        )}
      </AnimatePresence>

      {/* ── Content ── */}
      <div className="relative z-10 min-h-screen pt-16">
        {/* Shield tracker (replaces ProgressTracker) */}
        <ShieldTracker currentStep={currentStep} allComplete={isPrescription} />

        {/* Screen content */}
        <div className="px-8 pb-24">
          <AnimatePresence mode="wait">
            {contentReady && (
              <motion.div
                key={`content-${contentKey}`}
                initial={{ opacity: 0, scale: 0.98, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98, y: -8 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                <ContentComponent />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-30 flex items-center gap-4">
          <button
            onClick={handleBack}
            disabled={currentStep === 0 || forgeStep !== null}
            className={`
              px-6 py-3 rounded-lg text-[11px] font-mono font-semibold tracking-[0.12em] uppercase transition-all duration-300
              ${currentStep === 0 || forgeStep !== null
                ? 'bg-white/[0.02] text-white/10 border border-white/[0.03] cursor-not-allowed'
                : 'bg-transparent text-white/50 border border-white/[0.08] hover:text-white/80 hover:border-white/[0.18] hover:bg-white/[0.03]'}
            `}
          >
            Back
          </button>

          <div className="text-[10px] font-mono text-white/15 tracking-wider min-w-[40px] text-center">
            {currentStep + 1}/{STEPS.length}
          </div>

          <button
            onClick={() => goToStep(currentStep + 1)}
            disabled={currentStep === STEPS.length - 1 || forgeStep !== null}
            className={`
              px-6 py-3 rounded-lg text-[11px] font-mono font-semibold tracking-[0.12em] uppercase transition-all duration-300
              ${currentStep === STEPS.length - 1 || forgeStep !== null
                ? 'bg-white/[0.02] text-white/10 border border-white/[0.03] cursor-not-allowed'
                : 'bg-white/90 text-[#0F172A] hover:bg-white shadow-[0_0_20px_rgba(255,255,255,0.06)]'}
            `}
          >
            {currentStep === STEPS.length - 2 ? 'Forge Protocol' : 'Continue'}
          </button>
        </div>

        {/* Label */}
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
          <div className="bg-white/[0.03] border border-white/[0.06] rounded-full px-4 py-1.5 backdrop-blur-md">
            <span className="text-[9px] font-mono text-white/25 tracking-[0.2em] uppercase">
              Combined — Shield Tempering + Aurora Background
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
